import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Form, Overlay, Popover, Row } from "react-bootstrap";
import { useControl, useControlReducer } from "../../../controlContext";
import { LangEntrySelector } from "../recommendations/langEntrySelector";
import { useSource } from "../../../sourceContext";
import { NAR } from "../../../../../shared/Data/typeDirs";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import 'react-quill/dist/quill.core.css';
import '../../../../../assets/css/revised_css/components/controlManagerThemes/bootstrap/narrative.css'
import { useControlHeaderContext } from "../../../controlWrapperContext";
import { resetElementHeight } from "../utils/helpers";
// import { useDynamicTextAreaHeight } from "../utils/hooks";
import { Star } from "react-feather";
import { ChatWrapper } from '../../../../revised_components/ChatWrapper';
import {FormLabel} from '@mui/joy'

// Import Quill and Parchment for custom background class
const Quill = ReactQuill.Quill;
const Parchment = Quill.import("parchment");

const BgClass = new Parchment.Attributor.Class("bg", "bg", {
  scope: Parchment.Scope.INLINE,
});
Quill.register(BgClass, true);


export function NarrativeControl({
  name,
  idx,
  label
}) {
  const { [idx]: block } = useControl()
  const [showOverlay, setShowOverlay] = useState(false)
  const controlReducer = useControlReducer()
  const { [NAR]: narrative } = block
  const { settings: { readOnly: allReadOnly, lockMap } } = useControlHeaderContext()
  const readOnly = useMemo(() => (allReadOnly ? true : lockMap && lockMap[block._id]), [lockMap, allReadOnly, block._id])
  const source = useSource()
  const [forceUpdate, setForceUpdate] = useState(false)

  const [stateBuffer, setStateBuffer] = useState(false)
  const value = useMemo(() => stateBuffer || narrative.content, [stateBuffer, narrative.content])
  const [textContainerRef, textareaRef] = useDynamicTextAreaHeight(readOnly, value)

  const NARRATIVE_TEXTAREA_FIELD_NAME = `narrative_${narrative._id}`;
  const [isReady, setIsReady] = useState(false);

  function onChange(val, delta, source, editor) {
    // console.log('onChange setting narrative value', source, val)
    if (source === 'api' && !forceUpdate) return
    setStateBuffer(val)
  }
  useEffect(() => {
    if (stateBuffer === false) return
    let timeout_id = setTimeout(() => {
      controlReducer({
        idx, name, type: 'changed', update: {[name]: {
          ...block[NAR],
          content: stateBuffer
        }}, childAction: { type: 'changed', name: 'content', update: {content:stateBuffer}}
      })
      setStateBuffer(false)
    }, 200)
    return () => { clearTimeout(timeout_id) }
  }, [stateBuffer])

  useEffect(() => {
    // Wait one tick for the ref to be attached
    if (textareaRef.current) {
      setIsReady(true);
    }
  }, []);

  const onLangEntrySelect = useCallback((langEntry) => {
    console.log('current quill editor', textareaRef.current.getEditor())
    onChange(`${block[NAR].content}<p>${langEntry.content}</p>`, 1)
    // setTimeout(()=>setShowOverlay(false))
  }, [onChange, textareaRef, block])

  const modules = useMemo(
    () => ({ toolbar: { container: `#n${idx}`, handlers: { insertStar } } })
    , [idx])

  return (

    <div ref={textContainerRef} className="text-editor">
      <div>
        <ChatWrapper
          textSourceRef={textareaRef}
          fieldName={NARRATIVE_TEXTAREA_FIELD_NAME}
          reportId={block._reports_v2}
          blockId={block._id}
          blockName={block.blockName}
          onChange={onChange}
          onChatClose={() => {}}
          chatProps={{ includeFullText: true }}
        >
          <ReactQuill
            theme="snow"
            placeholder="Narrative"
            className="report-narrative-control"
            readOnly={readOnly}
            ref={textareaRef}
            modules={modules}
            value={value}
            onFocus={(range, triggeredBy, editor) => {
              if (!showOverlay && triggeredBy === 'user') {
                console.debug('focusing', editor)
                setTimeout(() => setShowOverlay(true), 50)
              }
            }}
            onChange={onChange}
            formats={[
              'header', 'font', 'size',
              'bold', 'italic', 'underline', 'strike', 'blockquote',
              'list', 'bullet', 'indent',
              'link', 'image', 'color', 'background', 'bg'
            ]}
          />
          <CustomToolbar narrativeIdx={idx} />
        </ChatWrapper>
      </div>       

      <Row className="g-0">
        <Form.Check key={`${narrative.isForExport}`}>
          <Form.Check.Label>Export Narrative</Form.Check.Label>
          <Form.Check.Input key={`${narrative.isForExport}`} type="checkbox" checked={narrative.isForExport} onChange={evt => {
            console.log('changing isForExport toggle', evt.currentTarget.checked, idx, name)
            controlReducer({
              type: 'changed',
              name,
              idx,
              update: {[NAR]:{...narrative, isForExport: evt.currentTarget.checked} },
              childAction: { type: 'changed', name: 'isForExport', update: {isForExport: evt.currentTarget.checked}}
            })
          }} />
        </Form.Check>
      </Row>
      <Overlay style={{zIndex: 1000}}
        target={textContainerRef.current}
        show={showOverlay}
        onHide={() => setShowOverlay(false)}
        placement="top-end"
        rootClose
      >
        <Popover className="report-narrative-popover">  
            <div className="g-0">
              <LangEntrySelector
                source={source}
                type={NAR}
                _subcategories={block.subcategories}
                onSelect={onLangEntrySelect}
              />
            </div>       
        </Popover>
      </Overlay >
    </div>


  )
}

function insertStar() {
  const cursorPosition = this.quill.getSelection().index;
  this.quill.insertText(cursorPosition, '★');
  this.quill.setSelection(cursorPosition + 1);
}

const CustomToolbar = ({ narrativeIdx }) => (
  <div key={narrativeIdx} id={`n${narrativeIdx}`} className="test-quill-tb">
    <select
      className="ql-header"
      defaultValue={'def'}
      onChange={(e) => e.persist()}
    >
      <option value="1"></option>
      <option value="2"></option>
      <option value="def"></option>
    </select>
    <button className="ql-bold"></button>
    <button className="ql-italic"></button>
    <select className="ql-color" defaultValue={'def'}>
      <option value="red"></option>
      <option value="green"></option>
      <option value="blue"></option>
      <option value="orange"></option>
      <option value="violet"></option>
      <option value="#d0d1d2"></option>
      <option value="def"></option>
    </select>
    <button className="ql-insertStar">
      <Star />
    </button>
  </div>
)

function useDynamicTextAreaHeight(readOnly, value) {
  const textareaRef = useRef()
  const textContainerRef = useRef()

  useEffect(() => {

    if (!readOnly) {
      textareaRef.current.getEditor().root.onkeydown = resetTextHeights
      textareaRef.current.getEditor().root.oninput = setTextHeights
      resetTextHeights(textareaRef, textContainerRef)
      setTextHeights(textareaRef, textContainerRef)
    } else {
      resetElementHeight(textContainerRef.current)
    }
  }, [value, readOnly])

  function resetTextHeights(taRef, tcRef) {
    textareaRef.current.getEditor().root.style.height = 'inherit'
    textContainerRef.current.style.minHeight = `${textareaRef.current.scrollHeight + 5}px`
  }

  function setTextHeights(taRef, tcRef) {
    // if(!textareaRef.current.style)return
    textContainerRef.current.style.minHeight = `${textareaRef.current.scrollHeight + 5}px`
    textareaRef.current.getEditor().root.style.height = `${textareaRef.current.getEditor().root.scrollHeight + 5}px`
  }

  return [textContainerRef, textareaRef]
}
